import { defineStore } from "pinia";
import { notNull } from "@/helpers/utility/Type";
import { useEvaluationOverviewStore } from "@editor/items/evaluation/evaluationOverviews.store";
import { useEvaluationStore } from "@editor/items/evaluation/evaluations.store";
import type { EvaluationItem } from "@editor/items/evaluation/EvaluationItem";
import type { EvaluationOverviewItem } from "@editor/items/evaluation/evaluationOverviews.store";

export const useEvaluationViewStore = defineStore("model/evaluationViewNew", {
  state: () => ({
    currentTabUid: "evaluation_0" as string | null,
    tabs: [
      {
        type: "evaluation",
        id: 0,
        uid: "evaluation_0"
      }
    ] as {
      type: "evaluation" | "evaluationOverview";
      uid: string;
      id: number;
    }[]
  }),
  getters: {
    openOverviewIds(state) {
      return state.tabs
        .filter((tab) => tab.type == "evaluationOverview")
        .map((tab) => tab.id) as readonly number[];
    },
    openEvaluationIds(state) {
      return state.tabs
        .filter((tab) => tab.type == "evaluation")
        .map((tab) => tab.id) as readonly number[];
    },
    tabOrder(state) {
      return state.tabs.slice(1).map((tab) => tab.uid);
    },
    tabItems(state) {
      return state.tabs.map((tab) => {
        if (tab.type == "evaluation") {
          return useEvaluationStore().byUid[tab.uid];
        } else {
          return useEvaluationOverviewStore().byUid[tab.uid];
        }
      });
    },
    tabItemsByUid(
      state
    ): Record<string, EvaluationItem | EvaluationOverviewItem> {
      return state.tabs.reduce((acc, tab) => {
        if (tab.type == "evaluation") {
          acc[tab.uid] = useEvaluationStore().byUid[tab.uid];
        } else {
          acc[tab.uid] = useEvaluationOverviewStore().byUid[tab.uid];
        }
        return acc;
      }, {} as Record<string, EvaluationItem | EvaluationOverviewItem>);
    },
    currentTabItem(state): EvaluationItem | EvaluationOverviewItem | null {
      if (state.currentTabUid == null) return null;

      return (
        useEvaluationStore().byUid[state.currentTabUid] ||
        useEvaluationOverviewStore().byUid[state.currentTabUid]
      );
    },
    currentEvaluation(state) {
      if (state.currentTabUid == null) return null;
      const evaluation = useEvaluationStore().byUid[state.currentTabUid];
      return evaluation ?? null;
    },
    currentEvaluationOverview(state) {
      const currentTab = state.tabs.find(
        (tab) => tab.uid == state.currentTabUid
      );
      if (currentTab == null) return null;
      if (currentTab.type != "evaluationOverview") return null;
      const evaluationOverview =
        useEvaluationOverviewStore().byUid[currentTab.uid];
      return evaluationOverview ?? null;
    }
  },
  actions: {
    openTab(
      tabEntry: {
        type: "evaluation" | "evaluationOverview";
        id: number;
        uid?: string;
      } | null
    ) {
      if (tabEntry == null) {
        this.currentTabUid = null;
        return;
      }

      const uid = tabEntry.uid ?? `${tabEntry.type}_${tabEntry.id}`;

      // the default tab is always open:
      if (uid === "evaluation_0") {
        this.currentTabUid = uid;
        return;
      }

      const tabIndex = this.tabs.findIndex((tab) => tab.uid == uid);
      if (tabIndex == -1) {
        this.tabs.push({
          type: tabEntry.type,
          id: tabEntry.id,
          uid: uid
        });
      }

      this.currentTabUid = uid;
    },
    closeTab(
      tabEntry:
        | { uid: string }
        | { type: "evaluationOverview" | "evaluation"; id: number }
    ) {
      const uid =
        "uid" in tabEntry ? tabEntry.uid : `${tabEntry.type}_${tabEntry.id}`;
      const index = this.tabs.findIndex((tab) => tab.uid == uid);

      if (index == -1) return;
      this.tabs.splice(index, 1);

      // select the next closest tab:
      if (this.currentTabUid == uid) {
        if (index >= 0) {
          const previousTab = this.tabs[index - 1];
          this.currentTabUid = previousTab.uid;
        } else {
          this.currentTabUid = null;
        }
      }
    },
    updateTabOrder(uids: string[]) {
      this.tabs = ["evaluation_0", ...uids]
        .map((uid) => this.tabs.find((tab) => tab.uid == uid))
        .filter(notNull);
    }
  }
});
